'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Calendar, Check, Clock, Plus, Shield, ShieldAlert, X, } from 'lucide-react';
import { useCopilot } from './context.js';
import { useDecideAction } from '../hooks/index.js';
// COP-135 D1 (task-08) — the card's outcome comes from its ONE ledger row,
// keyed by the draft id (`agent/tools/schedules.ts` records it at the single
// emit site). The card derives nothing from its own local state.
import { turnActionOutcomeLabel } from './turn-actions.js';
const PRIMARY_CTA = 'Approve & save schedule';
const SECONDARY_CTA = 'Reject';
const HEADER_KICKER = 'Schedule preview';
const SECTION_LABEL_PROMPT = 'Workflow prompt';
const SECTION_LABEL_ALLOWLIST = 'Tools this schedule may use';
const EMPTY_ALLOWLIST_HELPER = 'Add at least one tool the schedule may use.';
const PREAPPROVAL_TRIGGER_LABEL = 'Enable whole-plan preapproval';
const WHOLE_PLAN_PREAPPROVAL_CHECKBOX_LABEL = 'I understand. Enable whole-plan preapproval.';
const PROMPT_MAX_LENGTH = 1000;
const GENERIC_ERROR = 'Something went wrong. Please retry.';
const DEFAULT_ADMIN_SCHEDULE_HREF = '/admin/schedules';
/**
 * Stable error-code → user-visible copy map. Covers every Layer C code
 * surfaced by the PATCH + decide routes (Plan 02-04) plus the Layer A/B
 * codes that can bubble through to this card. Unmapped → generic fallback.
 *
 * Exported (rather than private as in `plan-card.tsx`'s `planErrorCopy`)
 * so integrators using the render-prop override can re-use the exact same
 * copy table.
 */
export function scheduleErrorCodeToCopy(code) {
    switch (code) {
        case 'preview_stale':
            return 'This preview is out of date. Reload and try again.';
        case 'draft_not_pending':
            return 'This preview is no longer pending. Ask the assistant to propose a new one.';
        case 'draft_not_found':
            return 'This preview is no longer pending. Ask the assistant to propose a new one.';
        case 'tool_not_in_allowlist':
            return 'One of the selected tools is not available on this track.';
        case 'track_access_denied':
            return "You don't have permission to schedule actions on this track. Ask a track admin to grant you a direct relation (member, admin, or viewer).";
        case 'empty_after_redaction':
            return 'The prompt was empty after secrets were removed. Provide a new prompt without secrets.';
        case 'empty_allowlist':
            return 'Add at least one tool the schedule may use.';
        case 'whole_plan_preapproved_unconfirmed':
            return 'You must check the whole-plan preapproval box before saving.';
        case 'cadence_below_15_minutes':
            return 'Cadences below 15 minutes are not supported.';
        case 'cadence_ambiguous':
            return 'The schedule cadence could not be understood. Ask the assistant to rephrase.';
        case 'cadence_composite_unsupported':
            return 'Composite cadences are not supported.';
        case 'cadence_one_shot_unsupported':
            return 'One-shot schedules are not supported.';
        case 'timezone_required':
            return 'Set a timezone before saving the schedule.';
        case 'invalid_timezone':
            return 'The provided timezone is invalid.';
        case 'allowlist_indeterminable':
            return 'The agent could not determine which tools this schedule should use.';
        case 'allowlist_user_named_ambiguous':
            return 'The tool name in the request matches multiple available tools.';
        case 'prompt_too_long':
            return 'The prompt is too long. Shorten it and try again.';
        case 'prompt_not_actionable':
            return 'The action text describes creating a schedule instead of the work to repeat. Say what should happen on each run.';
        default:
            return GENERIC_ERROR;
    }
}
/**
 * Render the schedule preview card. The default DOM contract is the
 * verbatim shape in UI-SPEC.md lines 235-484. Pass `children` to fully
 * override the DOM; the integrator receives the normalized
 * `SchedulePreviewPart` + UI state + bound handlers (no raw SSE payload).
 */
export function SchedulePreviewCard({ preview, initialTerminalState, resolvedAt: resolvedAtProp, adminScheduleHref = DEFAULT_ADMIN_SCHEDULE_HREF, className, children, actions, }) {
    const { renderProps, collapsePreapproval, togglePreapprovalChecked } = useSchedulePreviewCard({
        preview,
        initialTerminalState,
        resolvedAt: resolvedAtProp,
        adminScheduleHref,
        className,
        children,
    });
    if (children)
        return _jsx(_Fragment, { children: children(renderProps) });
    return (_jsx(SchedulePreviewCardDefaultView, { bag: renderProps, collapsePreapproval: collapsePreapproval, togglePreapprovalChecked: togglePreapprovalChecked, adminScheduleHref: adminScheduleHref, className: className, outcome: actions?.get(preview.scheduleDraftId)?.outcome }));
}
function useSchedulePreviewCard(props) {
    const { preview, initialTerminalState, resolvedAt: resolvedAtProp } = props;
    const { hub, sendMessage } = useCopilot();
    // Local draft state. Seeded from props; props are read-only — every
    // mutation flows through PATCH on commit.
    const [state, setState] = useState(initialTerminalState ?? 'pending');
    const [promptDraft, setPromptDraft] = useState(preview.canonicalPrompt);
    const [allowlistDraft, setAllowlistDraft] = useState(preview.toolAllowlist);
    const [payloadHash, setPayloadHash] = useState(preview.payloadHash);
    // Plan 02 WR-02 fix: when the model proposed whole-plan preapproval on
    // the initial preview, force the preapproval panel OPEN and clear the
    // checkbox so the user must explicitly re-acknowledge before Approve
    // becomes enabled. Mirrors the D-30 collapse-clears-checkbox invariant
    // enforced by `collapsePreapproval`. Without this, the card opened with
    // the panel collapsed, canApprove evaluated true (because !preapprovalExpanded),
    // and the user could click Approve without ever seeing the warning copy —
    // the server-side decideApprove path would then reject with
    // `whole_plan_preapproved_unconfirmed`, surfacing a confusing error
    // instead of the consent gate (D-29 / D-30).
    const [preapprovalExpanded, setPreapprovalExpanded] = useState(preview.wholePlanPreapproved === true);
    const [preapprovalChecked, setPreapprovalChecked] = useState(false);
    // Mirror of the server's persisted `wholePlanPreapproved` flag. Seeded from
    // the initial preview part and updated on every successful PATCH. Used by
    // `collapsePreapproval` so Cancel always synchronizes the server when the
    // flag is set — even in cases where the model preset it on first turn and
    // the user never ticked the local acknowledgement checkbox.
    const [serverWholePlanPreapproved, setServerWholePlanPreapproved] = useState(preview.wholePlanPreapproved === true);
    const [cronExpanded, setCronExpanded] = useState(false);
    const [addDropdownOpen, setAddDropdownOpen] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [resolvedAt, setResolvedAt] = useState(resolvedAtProp);
    const promptCommittedRef = useRef(preview.canonicalPrompt);
    // Plan 02 WR-01 fix: in-flight PATCH guard. setState('patching') is async;
    // a subsequent same-frame click (e.g. Cancel while a checkbox toggle's
    // PATCH is still in flight) re-reads state === 'pending' inside its
    // closure and dispatches a second PATCH with the SAME stale payloadHash,
    // which the server 409s and flips the card to 'stale-hash' for what
    // should be a no-op. The ref is synchronous and blocks the second call.
    const patchInFlightRef = useRef(false);
    // External terminal state updates (Plan 02-06 cross-message collector).
    useEffect(() => {
        if (initialTerminalState && initialTerminalState !== state && initialTerminalState !== 'pending') {
            setState(initialTerminalState);
            if (resolvedAtProp)
                setResolvedAt(resolvedAtProp);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTerminalState, resolvedAtProp]);
    const isHashFresh = state === 'pending';
    const canApprove = state === 'pending' &&
        allowlistDraft.length > 0 &&
        (!preapprovalExpanded || preapprovalChecked);
    const { submittedRef, applyDecideError } = useDecideAction({
        errorCopy: scheduleErrorCodeToCopy,
        resolveError: (e, copy) => {
            if (e.code === 'preview_stale') {
                setState('stale-hash');
                setErrorText(null);
                return;
            }
            if (e.code === 'draft_not_pending' || e.code === 'draft_not_found') {
                setState('expired');
                setErrorText(null);
                return;
            }
            setState('error');
            setErrorText(copy);
        },
    });
    const applyPatchError = (err) => {
        const e = (err ?? {});
        if (e.code === 'preview_stale') {
            setState('stale-hash');
            setErrorText(null);
            return;
        }
        if (e.code === 'draft_not_pending' || e.code === 'draft_not_found') {
            setState('expired');
            setErrorText(null);
            return;
        }
        setState('error');
        setErrorText(scheduleErrorCodeToCopy(e.code));
    };
    /**
     * Generic PATCH dispatcher. The Hub route accepts a narrow set of fields
     * (UpdateScheduleDraftBodySchema, Plan 02-01); the response carries the
     * regenerated payloadHash + the new expiresAt (Plan 02-04). We refresh
     * the hash + flip state back to 'pending' on success.
     */
    const patch = async (body) => {
        if (state !== 'pending')
            return;
        // WR-01: synchronous in-flight gate. The setState('patching') below is
        // batched; a second handler firing in the same React frame still
        // observes state === 'pending' and would issue a duplicate PATCH with
        // the stale payloadHash.
        if (patchInFlightRef.current)
            return;
        patchInFlightRef.current = true;
        setState('patching');
        setErrorText(null);
        try {
            // The route response is `ScheduleDraftResponseSchema` from
            // @verybigthings/trackframe-shared/schemas — declared on the typed
            // client endpoint so `HubClient._call` actually surfaces the parsed
            // body to us. (Without the declared `response`, the client returned
            // undefined and this whole branch was a silent no-op — P-41.5.)
            const response = (await hub.scheduleDraftsPatch({
                params: { id: preview.scheduleDraftId },
                body: { ...body, payloadHash },
            }));
            if (response?.payloadHash && typeof response.payloadHash === 'string') {
                setPayloadHash(response.payloadHash);
            }
            else if (typeof console !== 'undefined') {
                // Defensive: the response schema is wired up, so this branch should
                // be unreachable. If it fires, the typed client lost the response
                // body somehow — surface it in the browser console so the next
                // session has a breadcrumb to follow.
                console.warn('[SchedulePreviewCard] PATCH returned no payloadHash; card hash will stay at', payloadHash, 'for draft', preview.scheduleDraftId);
            }
            if (body.wholePlanPreapproved !== undefined) {
                setServerWholePlanPreapproved(body.wholePlanPreapproved);
            }
            setState('pending');
        }
        catch (err) {
            applyPatchError(err);
        }
        finally {
            patchInFlightRef.current = false;
        }
    };
    const commitPromptToServer = () => {
        if (state !== 'pending')
            return;
        if (promptDraft === promptCommittedRef.current)
            return;
        promptCommittedRef.current = promptDraft;
        void patch({ prompt: promptDraft });
    };
    const removeAllowlistChip = (toolName) => {
        if (state !== 'pending')
            return;
        const next = allowlistDraft.filter((t) => t !== toolName);
        setAllowlistDraft(next);
        void patch({ toolAllowlist: next });
    };
    const addAllowlistChip = (toolName) => {
        if (state !== 'pending')
            return;
        if (allowlistDraft.includes(toolName))
            return;
        const next = [...allowlistDraft, toolName];
        setAllowlistDraft(next);
        setAddDropdownOpen(false);
        void patch({ toolAllowlist: next });
    };
    const togglePreapprovalChecked = (checked) => {
        if (state !== 'pending')
            return;
        setPreapprovalChecked(checked);
        // D-26: checking the acknowledgement = explicit opt-in; both flags must
        // be persisted or decideApprove rejects with whole_plan_preapproved_unconfirmed.
        void patch({ wholePlanPreapproved: checked, wholePlanPreapprovedConfirmed: checked });
    };
    const collapsePreapproval = () => {
        setPreapprovalExpanded(false);
        // Per D-30: Cancel collapses AND clears the checkbox.
        setPreapprovalChecked(false);
        // Sync server when either the local checkbox is set OR the server still
        // has the flag on (the latter covers the "model preset wholePlanPreapproved
        // on first turn" path — the panel was force-opened, the user clicked
        // Cancel without ticking, and without this PATCH the server-side
        // `wholePlanPreapproved=true && confirmed=false` combo would trip
        // `whole_plan_preapproved_unconfirmed` at decideApprove time).
        if (preapprovalChecked || serverWholePlanPreapproved) {
            void patch({ wholePlanPreapproved: false, wholePlanPreapprovedConfirmed: false });
        }
    };
    const onApprove = async () => {
        if (submittedRef.current)
            return;
        if (!canApprove)
            return;
        submittedRef.current = true;
        setState('approving');
        setErrorText(null);
        try {
            const response = (await hub.scheduleDraftsDecide({
                params: { id: preview.scheduleDraftId },
                body: { decision: 'approve', payloadHash },
            }));
            setState('approved');
            setResolvedAt({
                scheduleId: response.scheduleId,
                nextRunAt: response.nextRunAt,
            });
            // D-22: continuation marker for the runtime branch (Plan 02-05).
            sendMessage({ pendingScheduleDraftId: preview.scheduleDraftId });
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const onReject = async () => {
        if (submittedRef.current)
            return;
        if (state !== 'pending')
            return;
        submittedRef.current = true;
        setState('rejecting');
        setErrorText(null);
        try {
            await hub.scheduleDraftsDecide({
                params: { id: preview.scheduleDraftId },
                body: { decision: 'reject', payloadHash },
            });
            setState('rejected');
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    /**
     * Stale-hash recovery (Gap 2 / CR-03). The previous implementation called
     * `setState('pending')` then `patch(...)`, but React `setState` is async —
     * the closure-captured `state` inside `patch` still read `'stale-hash'`, so
     * the in-helper guard silently dropped the request. Even if the guard
     * passed, re-PATCHing with the local (stale) hash would deterministically
     * 409 again. We now fetch the canonical state via `GET /schedule-drafts/:id`,
     * apply the server's payloadHash + payload to local state, then flip back
     * to 'pending'. On 404 (draft expired / rejected externally) we transition
     * to 'expired' so the user can dismiss the card.
     */
    const onReloadPreview = () => {
        if (state !== 'stale-hash')
            return;
        setErrorText(null);
        void (async () => {
            try {
                const fresh = (await hub.scheduleDraftsGet({
                    params: { id: preview.scheduleDraftId },
                }));
                if (fresh?.payloadHash && typeof fresh.payloadHash === 'string') {
                    setPayloadHash(fresh.payloadHash);
                }
                if (fresh?.payload) {
                    if (typeof fresh.payload.canonicalPrompt === 'string') {
                        setPromptDraft(fresh.payload.canonicalPrompt);
                        promptCommittedRef.current = fresh.payload.canonicalPrompt;
                    }
                    if (Array.isArray(fresh.payload.toolAllowlist)) {
                        setAllowlistDraft(fresh.payload.toolAllowlist);
                    }
                    if (typeof fresh.payload.wholePlanPreapproved === 'boolean') {
                        setPreapprovalChecked(fresh.payload.wholePlanPreapproved);
                    }
                }
                setState('pending');
            }
            catch (err) {
                const e = (err ?? {});
                if (e.code === 'draft_not_found') {
                    setState('expired');
                    return;
                }
                setState('error');
                setErrorText(scheduleErrorCodeToCopy(e.code));
            }
        })();
    };
    const renderProps = {
        preview,
        state,
        promptDraft,
        setPromptDraft,
        commitPromptToServer,
        allowlistDraft,
        removeAllowlistChip,
        addAllowlistChip,
        availableTools: preview.availableTools,
        preapprovalExpanded,
        setPreapprovalExpanded,
        preapprovalChecked,
        setPreapprovalChecked: togglePreapprovalChecked,
        addDropdownOpen,
        setAddDropdownOpen,
        cronExpanded,
        setCronExpanded,
        onApprove,
        onReject,
        onReloadPreview,
        errorText,
        isHashFresh,
        canApprove,
        resolvedAt,
    };
    return { renderProps, collapsePreapproval, togglePreapprovalChecked };
}
function SchedulePreviewCardDefaultView({ bag, collapsePreapproval, togglePreapprovalChecked, adminScheduleHref, className, outcome, }) {
    const { preview, state, promptDraft, setPromptDraft, commitPromptToServer, allowlistDraft, removeAllowlistChip, addAllowlistChip, preapprovalExpanded, setPreapprovalExpanded, preapprovalChecked, addDropdownOpen, setAddDropdownOpen, cronExpanded, setCronExpanded, onApprove, onReject, errorText, canApprove, onReloadPreview } = bag;
    const draftIdSafe = preview.scheduleDraftId;
    const cronId = `copilot-schedule-preview-card-cron-${draftIdSafe}`;
    const promptId = `copilot-schedule-preview-card-prompt-${draftIdSafe}`;
    const preapprovalPanelId = `copilot-schedule-preview-card-preapproval-panel-${draftIdSafe}`;
    const preapprovalHeadingId = `copilot-schedule-preview-card-preapproval-heading-${draftIdSafe}`;
    const allowlistEmpty = allowlistDraft.length === 0;
    const isDisabled = state !== 'pending';
    return (_jsxs("section", { className: [
            'copilot-schedule-preview-card',
            `copilot-schedule-preview-card-state-${state}`,
            className,
        ]
            .filter(Boolean)
            .join(' '), "data-tf-schedule-preview-card": "", "data-schedule-draft-id": draftIdSafe, "data-state": state, "data-allowlist-empty": allowlistEmpty ? '' : undefined, "data-preapproval-expanded": preapprovalExpanded ? '' : undefined, role: "region", "aria-label": "Schedule preview, approval required", children: [_jsxs("header", { className: "copilot-schedule-preview-card-header", children: [_jsxs("span", { className: "copilot-schedule-preview-card-kicker", children: [_jsx(Calendar, { "aria-hidden": "true", width: 16, height: 16 }), HEADER_KICKER] }), _jsx("h3", { className: "copilot-schedule-preview-card-cadence", children: preview.cadenceLabel }), _jsxs("button", { type: "button", className: "copilot-schedule-preview-card-cron-toggle", "aria-expanded": cronExpanded, "aria-controls": cronId, onClick: () => setCronExpanded(!cronExpanded), children: [_jsx(Clock, { "aria-hidden": "true", width: 12, height: 12 }), "Cron"] }), cronExpanded && (_jsx("code", { id: cronId, className: "copilot-schedule-preview-card-cron", children: preview.cronExpr })), _jsxs("p", { className: "copilot-schedule-preview-card-next-run", children: ["Next run: ", preview.nextRunAt] }), outcome !== undefined && (_jsx("p", { className: "copilot-schedule-preview-card-outcome", "data-schedule-outcome": outcome, children: turnActionOutcomeLabel(outcome) }))] }), state === 'stale-hash' && (_jsxs("div", { role: "alert", className: "copilot-schedule-preview-card-error-banner", children: [_jsx(AlertTriangle, { "aria-hidden": "true", width: 16, height: 16 }), _jsx("span", { children: "This preview is out of date. Reload and try again." }), _jsx("button", { type: "button", className: "copilot-schedule-preview-card-reload", onClick: onReloadPreview, children: "Reload preview" })] })), _jsxs("section", { className: "copilot-schedule-preview-card-prompt", children: [_jsx("label", { htmlFor: promptId, className: "copilot-schedule-preview-card-section-label", children: SECTION_LABEL_PROMPT }), _jsx("textarea", { id: promptId, value: promptDraft, onChange: (e) => setPromptDraft(e.currentTarget.value), onBlur: commitPromptToServer, maxLength: PROMPT_MAX_LENGTH, disabled: isDisabled }), _jsxs("small", { className: "copilot-schedule-preview-card-char-count", children: [promptDraft.length, " / ", PROMPT_MAX_LENGTH] })] }), _jsxs("section", { className: "copilot-schedule-preview-card-allowlist", children: [_jsx("div", { className: "copilot-schedule-preview-card-section-label", children: SECTION_LABEL_ALLOWLIST }), _jsxs("ul", { role: "list", className: "copilot-schedule-preview-card-chips", children: [allowlistDraft.map((tool) => (_jsxs("li", { className: "copilot-schedule-preview-card-chip", "data-tool-name": tool, children: [_jsx("span", { children: tool }), _jsx("button", { type: "button", className: "copilot-schedule-preview-card-chip-remove", "aria-label": `Remove ${tool} from this schedule's tools`, onClick: () => removeAllowlistChip(tool), disabled: isDisabled, children: _jsx(X, { "aria-hidden": "true", width: 12, height: 12 }) })] }, tool))), _jsxs("li", { children: [_jsxs("button", { type: "button", className: "copilot-schedule-preview-card-add-tool", "aria-haspopup": "listbox", "aria-expanded": addDropdownOpen, onClick: () => setAddDropdownOpen(!addDropdownOpen), disabled: isDisabled, children: [_jsx(Plus, { "aria-hidden": "true", width: 14, height: 14 }), "Add tool"] }), addDropdownOpen && (_jsxs("ul", { role: "listbox", className: "copilot-schedule-preview-card-add-dropdown", children: [preview.availableTools
                                                .filter((t) => !allowlistDraft.includes(t))
                                                .slice()
                                                .sort()
                                                .map((tool) => (_jsx("li", { role: "option", "aria-selected": "false", children: _jsx("button", { type: "button", onClick: () => addAllowlistChip(tool), children: tool }) }, tool))), preview.availableTools.filter((t) => !allowlistDraft.includes(t)).length === 0 && (_jsx("li", { role: "status", children: "All available tools already added." }))] }))] })] }), allowlistEmpty && (_jsx("p", { className: "copilot-schedule-preview-card-allowlist-helper", role: "status", children: EMPTY_ALLOWLIST_HELPER }))] }), preview.wholePlanPreapprovalUiEnabled === true && (_jsx("section", { className: "copilot-schedule-preview-card-preapproval", "data-expanded": preapprovalExpanded ? '' : undefined, children: !preapprovalExpanded ? (_jsxs("button", { type: "button", className: "copilot-schedule-preview-card-preapproval-trigger", "aria-expanded": "false", "aria-controls": preapprovalPanelId, onClick: () => setPreapprovalExpanded(true), disabled: isDisabled, children: [_jsx(Shield, { "aria-hidden": "true", width: 16, height: 16 }), PREAPPROVAL_TRIGGER_LABEL] })) : (_jsxs("div", { id: preapprovalPanelId, className: "copilot-schedule-preview-card-preapproval-panel", role: "group", "aria-labelledby": preapprovalHeadingId, children: [_jsxs("h4", { id: preapprovalHeadingId, className: "copilot-schedule-preview-card-preapproval-heading", children: [_jsx(ShieldAlert, { "aria-hidden": "true", width: 16, height: 16 }), "Whole-plan preapproval"] }), _jsx("p", { className: "copilot-schedule-preview-card-preapproval-warning", children: preview.wholePlanPreapprovedWarningText }), _jsxs("label", { className: "copilot-schedule-preview-card-preapproval-confirm", children: [_jsx("input", { type: "checkbox", checked: preapprovalChecked, onChange: (e) => togglePreapprovalChecked(e.currentTarget.checked), disabled: isDisabled }), _jsx("span", { children: WHOLE_PLAN_PREAPPROVAL_CHECKBOX_LABEL })] }), _jsx("button", { type: "button", className: "copilot-schedule-preview-card-preapproval-collapse", onClick: collapsePreapproval, disabled: isDisabled, children: "Cancel" })] })) })), state === 'pending' && (_jsxs("div", { className: "copilot-schedule-preview-card-actions", children: [_jsx("button", { type: "button", className: "copilot-schedule-preview-card-approve", onClick: onApprove, disabled: !canApprove, "aria-label": "Approve and save this schedule", children: PRIMARY_CTA }), _jsx("button", { type: "button", className: "copilot-schedule-preview-card-reject", onClick: onReject, "aria-label": "Reject this schedule preview", children: SECONDARY_CTA })] })), state === 'patching' && (_jsx("div", { className: "copilot-schedule-preview-card-state-patching", role: "status", children: "Saving changes\u2026" })), state === 'approving' && (_jsx("div", { className: "copilot-schedule-preview-card-state-approving", role: "status", children: "Saving schedule\u2026" })), state === 'rejecting' && (_jsx("div", { className: "copilot-schedule-preview-card-state-rejecting", role: "status", children: "Discarding preview\u2026" })), state === 'approved' && (_jsxs("div", { className: "copilot-schedule-preview-card-state-approved", role: "status", children: [_jsx(Check, { "aria-hidden": "true", width: 14, height: 14 }), _jsx("span", { children: "Schedule saved." }), _jsx("a", { className: "copilot-schedule-preview-card-open-admin", href: adminScheduleHref, target: "_blank", rel: "noreferrer", children: "Open in admin" })] })), state === 'rejected' && (_jsx("div", { className: "copilot-schedule-preview-card-state-rejected", role: "status", children: "Schedule discarded." })), state === 'expired' && (_jsx("div", { className: "copilot-schedule-preview-card-state-expired", role: "alert", children: "This preview expired. Ask the assistant to propose it again." })), state === 'error' && (_jsx("div", { className: "copilot-schedule-preview-card-state-error", role: "alert", children: errorText ?? GENERIC_ERROR }))] }));
}
//# sourceMappingURL=schedule-preview-card.js.map